import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, type SortingState, useReactTable } from '@tanstack/react-table'
import {
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  InfoIcon,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { createCustomSortingFn, useEmployeeFilters, useEmployeeFilterState } from 'components/hooks/quotes/useEmployeeFilters'
import { useKeyboardShortcuts } from 'components/hooks/useKeyboardShortcuts'
import { useQuote } from 'components/quote_explorer/QuoteProvider'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Checkbox, SolidAlertCircleIcon, SolidAlertTriangleIcon, TextInput, Tooltip } from 'slab'
import { formatMoney, fullStateNameFromAbbreviation } from 'utils'
import { type QuotingEmployee } from 'utils/web_api.schemas'

import { useQuotingEmployeeContext } from '../QuotingEmployeeContext'
import FilterMenu from './FilterMenu'
import RowButtons from './RowButtons'

const columnHelper = createColumnHelper<QuotingEmployee>()

type Props = {
  isLoading: boolean,
  employees: QuotingEmployee[],
  filteredStates: string[],
  onStateFilterChange: (state: string, checked: boolean) => void,
}

export const EmployeesTable = ({
  isLoading,
  employees,
  filteredStates,
  onStateFilterChange,
}: Props
) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([])

  const {
    searchQuery,
    setSearchQuery,
    filteredAgeBrackets,
    setFilteredAgeBrackets,
    filteredPayTypes,
    setFilteredPayTypes,
    handleAgeBracketFilterChange,
    handlePayTypeFilterChange,
  } = useEmployeeFilterState()

  const {
    selectedEmployee,
    onEmployeeSelected,
    filterAndSortEmployees,
    pinnedEmployeeIds,
    isAllowanceAffordable,
    monthlyAllowanceForEmployee,
    monthlyEmployeeOutOfPocketContribution,
    willEnrollEmployeeIds,
    onWillEnrollEmployeeIdsChange,
    isEmployeeInProjectedState,
    // getPlanAvailabilityForEmployee,
  } = useQuotingEmployeeContext()

  const { quote } = useQuote()

  const filterInputRef = useRef<HTMLInputElement>(null)

  useKeyboardShortcuts({ searchInputRef: filterInputRef })

  const { employeeCounts, additionalFilters } = useEmployeeFilters(employees, {
    searchQuery,
    filteredStates,
    filteredAgeBrackets,
    filteredPayTypes,
    pinnedEmployeeIds,
  })

  const handleClearAllFilters = () => {
    setSearchQuery('')
    filteredStates.forEach(state => onStateFilterChange(state, false))
    setFilteredAgeBrackets([])
    setFilteredPayTypes([])
  }

  const hasEmployerContributionAmounts = useMemo(() => {
    return employees.some((employee) => employee.employer_medical_contribution !== null)
  }, [employees])

  const hasProjectedStateEmployees = useMemo(() => {
    if (!quote || !quote.projected_data_state_codes || !isEmployeeInProjectedState) return false
    return employees.some(employee => isEmployeeInProjectedState(employee))
  }, [quote, employees, isEmployeeInProjectedState])

  const filteredEmployees = useMemo(() => {
    return filterAndSortEmployees(employees, searchQuery, additionalFilters)
  }, [employees, searchQuery, additionalFilters, filterAndSortEmployees])

  const getEmployeeCostDifference = (employee: QuotingEmployee) => {
    const employeeCurrentContribution = employee.employee_medical_contribution
    if (employeeCurrentContribution == null) return 0
    const contributionAmount = monthlyEmployeeOutOfPocketContribution(employee)
    return employeeCurrentContribution - contributionAmount
  }

  const getEmployerCostDifference = (employee: QuotingEmployee) => {
    const employersCurrentContribution = employee.employer_medical_contribution
    if (employersCurrentContribution == null) return 0
    const contributionAmount = monthlyAllowanceForEmployee(employee)
    return employersCurrentContribution - contributionAmount
  }

  // const getTotalPlansForEmployee = (employee: QuotingEmployee) => {
  //   const planAvailability = getPlanAvailabilityForEmployee(employee)
  //   if (!planAvailability?.plan_counts) return 0
  //   return Object.values(planAvailability.plan_counts).reduce((sum, count) => sum + count, 0)
  // }

  const customSortingFn = createCustomSortingFn<QuotingEmployee>(sorting, pinnedEmployeeIds)

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => {
          const employeeResult = info.row.original
          return (
            <div>
              <div className='group-hover/row:underline flex items-center gap-2'>
                {employeeResult.name}
                {isAllowanceAffordable && !isAllowanceAffordable(employeeResult) && (
                  <Tooltip content='Allowance is unaffordable'>
                    <div className='text-red-400'>
                      <SolidAlertTriangleIcon size={18} />
                    </div>
                  </Tooltip>
                )}
              </div>
              {/*
              <div className='body-sm text-gray-500'>
                Can choose from {getTotalPlansForEmployee(employeeResult)} plans
              </div>
              */}
            </div>
          )
        },
      }),
      columnHelper.display({
        id: 'location',
        header: 'Location',
        cell: (info) => {
          const employeeResult = info.row.original
          return fullStateNameFromAbbreviation(employeeResult.rating_area_code.substring(0, 2))
        },
      }),
      columnHelper.accessor(row => monthlyAllowanceForEmployee(row), {
        id: 'budget',
        enableSorting: true,
        sortingFn: customSortingFn(monthlyAllowanceForEmployee),
        header: (info) => (
          <SortableHeader<QuotingEmployee>
            {...info}
            headerText='Monthly Allowance'
            align='right'
            leftIcon={
              hasProjectedStateEmployees
                ? (
                  <Tooltip content='You have employees in states that have not filed finalized rates for 2026. Allowances are subject to change once finalized rates are available.'>
                    <div className='text-amber-500' data-testid='projected-state-warning'>
                      <SolidAlertCircleIcon size={16} />
                    </div>
                  </Tooltip>
                  )
                : undefined
            }
          />
        ),
        cell: (info) => {
          const employeeResult = info.row.original
          return (
            <div className='flex items-center justify-end'>
              <Tooltip content="The amount you're contributing to the employee's allowance each month.">
                <span className='tabular-nums'>
                  {formatMoney(monthlyAllowanceForEmployee(employeeResult))}
                </span>
              </Tooltip>
            </div>
          )
        },
      }),
      columnHelper.display({
        id: 'willEnroll',
        header: 'Will Enroll',
        cell: (info) => {
          const employee = info.row.original
          const isChecked = willEnrollEmployeeIds.has(employee.id)

          return (
            <div className='flex items-center justify-center'>
              <Checkbox
                checked={isChecked}
                onChange={() => {
                  const newEnrolledIds = new Set(willEnrollEmployeeIds)
                  if (isChecked) {
                    newEnrolledIds.delete(employee.id)
                  } else {
                    newEnrolledIds.add(employee.id)
                  }
                  onWillEnrollEmployeeIdsChange(newEnrolledIds)
                }}
              />
            </div>
          )
        },
      }),
      columnHelper.accessor(getEmployerCostDifference, {
        id: 'costDifference',
        enableSorting: true,
        sortingFn: customSortingFn(getEmployerCostDifference),
        header: (info) => (
          <div className='flex items-center justify-end gap-2'>
            <SortableHeader<QuotingEmployee>
              {...info}
              headerText='Employer Cost Difference'
              align='right'
            />
            <Tooltip content="Cost difference between the employer contribution for the employee's current plan and the configured Thatch allowance.">
              <InfoIcon className='w-3 h-3 text-gray-700' />
            </Tooltip>
          </div>
        ),
        cell: (info) => {
          const employeeResult = info.row.original
          const employersCurrentContribution = employeeResult.employer_medical_contribution
          if (employersCurrentContribution == null) {
            return '-'
          }
          const contributionAmount = monthlyAllowanceForEmployee(employeeResult)
          const differenceFromCurrentPlan = employersCurrentContribution - contributionAmount
          const employerWouldPayMoreThanCurrentPlan = contributionAmount > employersCurrentContribution

          return (
            <div className='flex items-center justify-end gap-1'>
              <span className='tabular-nums'>
                {formatMoney(Math.abs(differenceFromCurrentPlan))}
              </span>
              {Math.abs(differenceFromCurrentPlan) > 0 && (
                employerWouldPayMoreThanCurrentPlan
                  ? <ArrowUpRightIcon className='w-4 h-4 text-red-400' />
                  : <ArrowDownRightIcon className='w-4 h-4 text-green-400' />
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor(getEmployeeCostDifference, {
        id: 'employeeCostDifference',
        enableSorting: true,
        sortingFn: customSortingFn(getEmployeeCostDifference),
        header: (info) => (
          <div className='flex items-center justify-end gap-2'>
            <SortableHeader<QuotingEmployee>
              {...info}
              headerText='Employee Cost Difference'
              align='right'
            />
            <Tooltip content="Cost difference between the employee's out-of-pocket costs in their current plan versus the estimated out-of-pocket costs with the configured Thatch allowance.">
              <InfoIcon className='w-3 h-3 text-gray-700' />
            </Tooltip>
          </div>
        ),
        cell: (info) => {
          const employeeResult = info.row.original
          const employeeCurrentContribution = employeeResult.employee_medical_contribution
          if (employeeCurrentContribution == null) {
            return '-'
          }

          const contributionAmount = monthlyEmployeeOutOfPocketContribution(employeeResult)
          const differenceFromCurrentPlan = employeeCurrentContribution - contributionAmount
          const employeeWouldPayMoreThanCurrentPlan = contributionAmount > employeeCurrentContribution

          return (
            <div className='flex items-center justify-end gap-1'>
              <span className='tabular-nums'>
                {formatMoney(Math.abs(differenceFromCurrentPlan))}
              </span>
              {Math.abs(differenceFromCurrentPlan) > 0 && (
                employeeWouldPayMoreThanCurrentPlan
                  ? <ArrowUpRightIcon className='w-4 h-4 text-red-400' />
                  : <ArrowDownRightIcon className='w-4 h-4 text-green-400' />
              )}
            </div>
          )
        },
      }),
      columnHelper.display({
        id: 'buttons',
        header: '',
        cell: (info) => (
          <RowButtons
            employee={info.row.original}
            employeeSelected={selectedEmployee?.id === info.row.original.id}
            allowance={monthlyAllowanceForEmployee(info.row.original)}
          />
        )
      })
    ]
  }, [employees, selectedEmployee, isAllowanceAffordable, monthlyAllowanceForEmployee, monthlyEmployeeOutOfPocketContribution, customSortingFn, willEnrollEmployeeIds, onWillEnrollEmployeeIdsChange])

  const table = useReactTable({
    columns,
    data: filteredEmployees,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      pagination,
      columnVisibility: {
        costDifference: hasEmployerContributionAmounts,
        employeeCostDifference: hasEmployerContributionAmounts && !quote?.has_flat_allowances,
      },
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    meta: {
      setSorting,
    }
  })

  return (
    <>
      <div className='mb-4'>
        <div className='flex items-center gap-4'>
          <div className='flex-grow'>
            <TextInput
              placeholder='Find an employee'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              ref={filterInputRef}
              className='h-10'
            />
          </div>
          <div className='flex-shrink-0'>
            <FilterMenu
              employeeCountByState={employeeCounts.employeeCountByState}
              employeeCountByPayType={employeeCounts.employeeCountByPayType}
              employeeCountByAgeBrackets={employeeCounts.employeeCountByAgeBrackets}
              filteredPayTypes={filteredPayTypes}
              filteredStates={filteredStates}
              filteredAgeBrackets={filteredAgeBrackets}
              onStateFilterChange={onStateFilterChange}
              onAgeBracketFilterChange={ageBracket => {
                handleAgeBracketFilterChange(ageBracket, !filteredAgeBrackets.includes(ageBracket))
              }}
              onPayTypeFilterChange={payType => {
                handlePayTypeFilterChange(payType, !filteredPayTypes.includes(payType))
              }}
              onClearAllFilters={handleClearAllFilters}
            />
          </div>
        </div>
        <p className='text-xs text-gray-500 mt-2'>
          {filteredEmployees.length}{' '}
          {searchQuery.trim().length + filteredStates.length + filteredPayTypes.length + filteredAgeBrackets.length > 0 && (
            <>of {employees.length}</>
          )}
          {' '}employees
        </p>
      </div>
      <div className='overflow-x-auto'>
        <TanstackTable
          table={table}
          isLoading={isLoading}
          noDataMessage='No employee data available.'
          paginationLabelSingular='employee'
          headerRowClasses='bg-white border-none ring-none text-xs !text-gray-500'
          rowClasses={(row) => {
            if (selectedEmployee?.id === row.original.id) {
              return 'bg-gray-100'
            }
          }}
          onRowClick={({ event, row }) => {
            const target = event.target as HTMLElement
            const isButtonClick = target.closest('button') !== null
            const isCheckboxClick = target.closest('input[type="checkbox"]') !== null

            if (!isButtonClick && !isCheckboxClick) {
              onEmployeeSelected(row.original)
            }
          }}
          hideTableOutlines
        />
      </div>
    </>
  )
}

export default EmployeesTable
